/**
 * /join/:token - an invite link from the team. No application: the person types their email, the
 * link is used up, and they log in with the normal email code.
 *  - Free access link: a free year, as if they paid
 *  - Pre-approved link: no application; they choose the yearly access after logging in
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CREATOR_NAME } from '@/config/brandConfig'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

type LinkState = 'loading' | 'active' | 'used' | 'expired' | 'revoked' | 'invalid' | 'error'
type Grant = 'free_year' | 'approved'

const CLOSED: Record<string, { title: string; text: string }> = {
  used: { title: 'This link was already used', text: 'Each invite link works once. Ask for a new one, or log in if it was yours.' },
  expired: { title: 'This link has expired', text: 'Ask for a new invite link, or apply instead.' },
  revoked: { title: 'This link was turned off', text: 'Ask for a new invite link, or apply instead.' },
  invalid: { title: 'This link does not work', text: 'Check that you copied the whole link, or apply instead.' },
  error: { title: 'Something went wrong', text: 'Please refresh the page and try again.' }
}

export default function JoinPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [state, setState] = useState<LinkState>('loading')
  const [grant, setGrant] = useState<Grant>('approved')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [knownEmail, setKnownEmail] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch(`${API_URL}/api/subscriptions/invite-links/${encodeURIComponent(token || '')}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return
        if (data.grant) setGrant(data.grant)
        setState(['active', 'used', 'expired', 'revoked'].includes(data.state) ? data.state : 'invalid')
      })
      .catch(() => !cancelled && setState('error'))
    return () => { cancelled = true }
  }, [token])

  const goToLogin = (addr: string) => navigate(`/login?email=${encodeURIComponent(addr)}`)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setMessage(null)
    const addr = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) {
      setMessage('Please enter a valid email address.')
      return
    }
    setBusy(true)
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/invite-links/${encodeURIComponent(token || '')}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: addr })
      })
      const data = await res.json().catch(() => ({}))
      if (data.ok) {
        goToLogin(data.email || addr)
        return
      }
      if (data.reason === 'already_member') {
        setKnownEmail(data.email || addr)
        setMessage('You already have access with this email. Just log in.')
      } else if (data.reason === 'already_approved') {
        setKnownEmail(data.email || addr)
        setMessage('This email is already approved. Just log in.')
      } else if (data.reason === 'bad_email') {
        setMessage('Please enter a valid email address.')
      } else if (CLOSED[data.reason]) {
        setState(data.reason)
      } else if (res.status === 429) {
        setMessage('Too many tries. Please wait a minute and try again.')
      } else {
        setMessage('Something went wrong. Please try again.')
      }
    } catch {
      setMessage('Could not connect. Check your internet and try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#ece6dd] flex items-center justify-center px-4 py-10">
      <div className="bg-white/80 rounded-3xl shadow-lg p-8 max-w-sm w-full text-center" data-testid="join-card">
        {state === 'loading' && (
          <div className="py-10 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6b7850]" />
          </div>
        )}

        {state === 'active' && (
          <>
            <div className="text-4xl mb-3">{grant === 'free_year' ? '🎁' : '✨'}</div>
            <span className="inline-block text-xs font-semibold uppercase tracking-wide text-[#6b7850] bg-[#6b7850]/10 rounded-full px-3 py-1 mb-3">
              {grant === 'free_year' ? 'Free access' : 'Pre-approved'}
            </span>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">You're invited to {CREATOR_NAME}'s world</h1>
            <p className="text-sm text-gray-700 mb-6 leading-relaxed">
              {grant === 'free_year'
                ? 'Your first year is on us: no application and no payment. Enter your email to get in.'
                : 'You are pre-approved: no application needed. Enter your email, log in, and choose your yearly access.'}
            </p>
            <form onSubmit={submit} className="space-y-3">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setKnownEmail(null) }}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#6b7850]"
                data-testid="join-email"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full py-3 bg-[#6b7850] text-white font-semibold rounded-xl hover:bg-[#5c5b44] transition-colors disabled:opacity-60"
                data-testid="join-submit"
              >
                {busy ? 'One moment…' : 'Continue'}
              </button>
            </form>
            {message && <p className="text-sm text-gray-800 mt-4" data-testid="join-message">{message}</p>}
            {knownEmail && (
              <button onClick={() => goToLogin(knownEmail)} className="mt-3 w-full py-3 border border-[#6b7850] text-[#6b7850] font-semibold rounded-xl hover:bg-[#6b7850]/10">
                Log in
              </button>
            )}
            <p className="text-xs text-gray-500 mt-6">This link works once. You will log in with a code sent to your email.</p>
          </>
        )}

        {CLOSED[state] && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="join-closed">{CLOSED[state].title}</h1>
            <p className="text-sm text-gray-700 mb-8 leading-relaxed">{CLOSED[state].text}</p>
            <div className="space-y-3">
              <button onClick={() => navigate('/apply')} className="w-full py-3 bg-[#6b7850] text-white font-semibold rounded-xl hover:bg-[#5c5b44] transition-colors">
                Apply
              </button>
              <button onClick={() => navigate('/login')} className="w-full py-3 border border-[#6b7850] text-[#6b7850] font-semibold rounded-xl hover:bg-[#6b7850]/10">
                Log in
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
